import type { ESidebarView } from "@frontend-shared/types";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { memo } from "react";

import { cn } from "@/lib/utils";

interface ISidebarViewTabProps {
  view: ESidebarView;
  label: string;
  // NOTE: true while a dragged chat is being held over this tab, before the
  // hold is long enough to open it. The switch owns that timing; this is only
  // how the tab shows it.
  isDragOver: boolean;
  // NOTE: the product tour anchors on these ids, so a tab that carries one must
  // keep it verbatim — a step whose target is missing highlights nothing and
  // fails silently.
  tourId?: string;
}

/**
 * NOTE: NSAI-6342 — one tab of the sidebar's view switch.
 */
const SidebarViewTabComponent = ({
  view,
  label,
  isDragOver,
  tourId,
}: ISidebarViewTabProps): JSX.Element => (
  <TabsPrimitive.Trigger
    id={tourId}
    value={view}
    // NOTE: how the switch finds the tab under a dragged chat's pointer.
    data-sidebar-view={view}
    // NOTE: the label is truncated in the narrowest locales, so the full string
    // stays reachable as the native tooltip.
    title={label}
    data-testid={`sidebar-view-switch-${view}`}
    className={cn(
      // NOTE: NSAI-6342 — an underlined tab rather than the filled segmented
      // track mobile draws. In a 256px sidebar a three-up filled track eats the
      // full width and reads as a button bar competing with the nav above it;
      // the underline spends no ink on the tabs you did not pick, which is what
      // keeps the chat list the loudest thing in the pane.
      //
      // NOTE: each tab is as wide as its label rather than a third of the row:
      // "Assistant chats" needs ~88px in English and ~118px in Russian, where
      // an equal third of a 256px sidebar is ~78px, so equal thirds ellipsised
      // the middle tab in every locale. `min-w-0` + `truncate` stay as the
      // backstop for a locale longer than all three together.
      "group/tab relative flex min-w-0 shrink items-center justify-center",
      "rounded-t-lg px-1.5 pb-2 pt-1.5 text-xs font-medium",
      "text-sidebar-foreground-secondary transition-colors duration-200 ease-out",
      "hover:text-sidebar-foreground",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring",
      "data-[state=active]:text-sidebar-foreground",
      // NOTE: the pane a held drag is about to open lights up before it opens,
      // so the switch is legible as a target rather than as a jump.
      isDragOver && "bg-sidebar-accent/60 text-sidebar-foreground",
    )}
  >
    <span className="truncate">{label}</span>
    {/* NOTE: the indicator belongs to each tab rather than being one thumb slid
        across the row: a shared thumb has to be measured, and a measured offset
        is one more thing to get wrong in RTL and at the moment the sidebar
        mounts hidden. Scaling it out of the centre reads as the same motion. */}
    <span
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-x-1.5 -bottom-px h-0.5 rounded-full bg-primary",
        "origin-center scale-x-0 opacity-0 transition duration-200 ease-out",
        "group-data-[state=active]/tab:scale-x-100 group-data-[state=active]/tab:opacity-100",
      )}
    />
  </TabsPrimitive.Trigger>
);

export const SidebarViewTab = memo(SidebarViewTabComponent);
